import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const TaskAnalytics = ({ tasks, familyMembers }) => {
  const stats = useMemo(() => {
    const total = tasks?.length;
    const completed = tasks?.filter(t => t?.isComplete)?.length;
    const active = total - completed;
    const overdue = tasks?.filter(t => 
      new Date(t?.dueDate) < new Date() && !t?.isComplete
    )?.length;
    
    const avgProgress = total > 0 
      ? Math.round(tasks?.reduce((sum, t) => sum + t?.progress, 0) / total)
      : 0;

    // Member participation
    const memberStats = familyMembers?.map(member => {
      const memberTasks = tasks?.filter(t => t?.assignedMembers?.includes(member?.id));
      const memberCompleted = memberTasks?.filter(t => {
        const status = t?.completionStatus?.[member?.id];
        return status?.completed;
      })?.length;
      
      return {
        ...member,
        taskCount: memberTasks?.length,
        completedCount: memberCompleted,
        completionRate: memberTasks?.length > 0 
          ? Math.round((memberCompleted / memberTasks?.length) * 100)
          : 0
      };
    });

    const topPerformer = memberStats?.reduce((top, member) => 
      member?.completionRate > (top?.completionRate || 0) ? member : top
    , {});

    return {
      total,
      completed,
      active,
      overdue,
      avgProgress,
      topPerformer
    };
  }, [tasks, familyMembers]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {/* Total Tasks */}
      <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="list" size={18} className="text-primary" />
          <span className="text-xs font-medium text-muted-foreground">Total Tasks</span>
        </div>
        <div className="text-2xl font-bold text-foreground">{stats?.total}</div>
      </div>

      {/* Active Tasks */}
      <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="activity" size={18} className="text-blue-500" />
          <span className="text-xs font-medium text-muted-foreground">Active</span>
        </div>
        <div className="text-2xl font-bold text-foreground">{stats?.active}</div>
      </div>

      {/* Completed Tasks */}
      <div className="bg-gradient-to-br from-success/10 to-success/5 border border-success/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="check-circle" size={18} className="text-success" />
          <span className="text-xs font-medium text-muted-foreground">Completed</span>
        </div>
        <div className="text-2xl font-bold text-foreground">{stats?.completed}</div>
      </div>

      {/* Overdue Tasks */}
      <div className="bg-gradient-to-br from-destructive/10 to-destructive/5 border border-destructive/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="alert-circle" size={18} className="text-destructive" />
          <span className="text-xs font-medium text-muted-foreground">Overdue</span>
        </div>
        <div className="text-2xl font-bold text-foreground">{stats?.overdue}</div>
      </div>

      {/* Average Progress */}
      <div className="bg-gradient-to-br from-warning/10 to-warning/5 border border-warning/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="trending-up" size={18} className="text-warning" />
          <span className="text-xs font-medium text-muted-foreground">Avg Progress</span>
        </div>
        <div className="text-2xl font-bold text-foreground">{stats?.avgProgress}%</div>
      </div>

      {/* Top Performer */}
      <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 border border-secondary/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="award" size={18} className="text-secondary" />
          <span className="text-xs font-medium text-muted-foreground">Top Performer</span>
        </div>
        <div className="flex items-center gap-2">
          {stats?.topPerformer?.name ? (
            <>
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                style={{ backgroundColor: stats?.topPerformer?.color }}
              >
                {stats?.topPerformer?.initials}
              </div>
              <span className="text-sm font-bold text-foreground truncate">
                {stats?.topPerformer?.name}
              </span>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskAnalytics;